// frontend/src/pages/ClientDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClientById, Client } from './services/clientsAPI';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import styles from './ClientDetailPage.module.css';

interface ClientData {
    clientInfo: { ID: number; NOME: string; CIDADE: string; ESTADO: string; };
    salesHistory: { DATA: string; Item: string; Quantidade: number; Venda: number; Vendedor: string; }[];
    stats: { totalSales: number; totalTransactions: number; firstSale: string; lastSale: string; };
}

const ClientDetailPage: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const [clientData, setClientData] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!clientId) return;
        const loadClientData = async () => {
            try {
                setLoading(true);
                const response = await getClientById(clientId);
                setClientData(response.data);
            } catch (err) {
                setError('Falha ao carregar os detalhes do cliente.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadClientData();
    }, [clientId]);

    if (loading) return <LoadingSpinner message="Carregando detalhes do cliente..." />;
    if (error) return <p className={styles.errorText}>{error}</p>;
    if (!clientData) return <p className={styles.noDataText}>Cliente não encontrado.</p>;

    const formatCPFCNPJ = (documento: string) => {
        if (!documento) return '';
        const cleaned = documento.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (cleaned.length === 14) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return documento;
    };

    return (
        <div className={styles.pageContainer}>
            <Link to="/clientes" className={styles.backLink}>&larr; Voltar para Clientes</Link>
            <h1 className={styles.clientName}>{clientData.NOME}</h1>
            <p className={styles.clientLocation}>
                {clientData.FANTASIA && <span>{clientData.FANTASIA} | </span>}
                {clientData.CIDADE} - {clientData.ESTADO}
            </p>

            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <h4>CPF/CNPJ</h4>
                    <p>{formatCPFCNPJ(clientData.DOCUMENTO)}</p>
                </div>
                <div className={styles.statCard}>
                    <h4>Telefone</h4>
                    <p>{clientData.DDD && clientData.TELEFONE ? `(${clientData.DDD}) ${clientData.TELEFONE}` : clientData.TELEFONE || 'N/D'}</p>
                </div>
                <div className={styles.statCard}>
                    <h4>Email</h4>
                    <p>{clientData.EMAIL || 'N/D'}</p>
                </div>
                <div className={styles.statCard}>
                    <h4>Status</h4>
                    <p>{clientData.ATIVO === 'S' ? 'Ativo' : 'Inativo'}</p>
                </div>
            </div>

            <div className={styles.detailsSection}>
                <h2>Informações Cadastrais</h2>
                <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                        <strong>ID:</strong> {clientData.ID}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Tipo:</strong> {clientData.TIPOPESSOA === 'J' ? 'Jurídica' : clientData.TIPOPESSOA === 'F' ? 'Física' : 'Estrangeiro'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Endereço:</strong> {clientData.ENDERECO}, {clientData.NUMERO} {clientData.COMPLEMENTO && `- ${clientData.COMPLEMENTO}`}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Bairro:</strong> {clientData.BAIRRO || 'N/D'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>CEP:</strong> {clientData.CEP || 'N/D'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>País:</strong> {clientData.PAIS || 'Brasil'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Website:</strong> {clientData.SITE || 'N/D'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Data de Cadastro:</strong> {clientData.DATACAD ? new Date(clientData.DATACAD).toLocaleDateString('pt-BR') : 'N/D'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Último Pedido:</strong> {clientData.DATA_PEDIDO ? new Date(clientData.DATA_PEDIDO).toLocaleDateString('pt-BR') : 'N/D'}
                    </div>
                    <div className={styles.detailItem}>
                        <strong>Limite de Crédito:</strong> {clientData.LIMITE ? clientData.LIMITE.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'N/D'}
                    </div>
                </div>
            </div>

            {clientData.OBS && (
                <div className={styles.obsSection}>
                    <h2>Observações</h2>
                    <p>{clientData.OBS}</p>
                </div>
            )}
        </div>
    );
};

export default ClientDetailPage;